import { useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import { GetServerSidePropsContext } from 'next'
import { useRouter } from 'next/router'
import {
  Layout,
  Head,
  Title,
  Button,
  ListingCard,
  ListingModal,
  FilterModal,
  ErrorBoundary,
  Loading,
} from '@/components'

import { useSession } from 'next-auth/react'
import { AdjustmentsIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import { useLazyQuery } from '@apollo/client'
import { ALL_LISTINGS_QUERY } from '@/queries'

const DynamicMap = dynamic(() => import('@/components/ExploreMap'), {
  ssr: false,
})

const defaultFilters = {
  rentLow: null,
  rentHigh: null,
  accessibility: null,
  bathrooms: null,
  bedrooms: null,
  laundry: null,
  listingType: null,
  parkingAvailable: null,
  utilitiesIncluded: null,
  airConditioning: null,
  dishwasher: null,
  petsWelcome: null,
}

export default function Explore({ pages, query: { city, latitude, longitude } }) {
  const [getListings, { loading, error, data, fetchMore, called }] = useLazyQuery(ALL_LISTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
  })

  const { data: session } = useSession()
  const [open, setOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const router = useRouter()
  const [bounds, setBounds] = useState<any>()
  const [searchFacets, setSearchFacets] = useState<any>(defaultFilters)

  const handleSearchUpdate = (data) => {
    const facets = { ...defaultFilters, ...data }
    if (bounds) {
      fetchBylatitudelongitude({
        bounds,
        facets,
      })
    } else {
      fetchByLatLng(facets)
    }
    setSearchFacets(facets)
  }

  const fetchByLatLng = async (facets) => {
    try {
      getListings({
        variables: {
          first: 10,
          latitude: Number(latitude),
          longitude: Number(longitude),
          filters: facets,
        },
      })
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    fetchByLatLng(searchFacets)
  }, [])

  useEffect(() => {
    if (router.query.listingid) {
      setOpen(true)
    } else {
      setOpen(false)
    }

    if (router.query.bbox) {
      const bbox = (router.query.bbox as string).split(',').map((val) => Number(val))
      setBounds({ maxLat: bbox[3], minLat: bbox[1], maxLng: bbox[2], minLng: bbox[0] })
      fetchBylatitudelongitude({
        bounds: { maxLat: bbox[3], minLat: bbox[1], maxLng: bbox[2], minLng: bbox[0] },
        facets: searchFacets,
      })
    }
  }, [router.query])

  const fetchBylatitudelongitude = async ({
    bounds,
    facets,
    cursor = null,
  }: {
    cursor?: string
    bounds: { maxLat: number; minLat: number; maxLng: number; minLng: number }
    facets: any
  }) => {
    try {
      const searchrange = {
        lessLat: bounds.maxLat > 1 ? bounds.maxLat : bounds.minLat,
        greatLat: bounds.minLat > 1 ? bounds.minLat : bounds.maxLat,
        lessLng: bounds.maxLng > 1 ? bounds.maxLng : bounds.minLng,
        greatLng: bounds.minLng > 1 ? bounds.minLng : bounds.maxLng,
      }

      if (cursor) {
        fetchMore({
          variables: { after: cursor, searchrange, filters: facets },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            fetchMoreResult.queryListings.edges = [
              ...prevResult.queryListings.edges,
              ...fetchMoreResult.queryListings.edges,
            ]
            return fetchMoreResult
          },
        })
      } else {
        getListings({
          variables: {
            first: 10,
            searchrange,
            filters: facets,
          },
        })
      }
    } catch (err) {}
  }

  const handleViewportChange = async ({
    bounds,
  }: {
    latitude: number
    longitude: number
    zoom: number
    bounds: { maxLat: number; minLat: number; maxLng: number; minLng: number }
  }) => {
    setBounds(bounds)
    fetchBylatitudelongitude({
      bounds,
      facets: searchFacets,
    })
  }

  const listings = data?.queryListings?.edges || []
  return (
    <Layout>
      <Head title="Explore" />
      <div className="lg:grid grid-cols-12 relative pt-0">
        <div
          className={clsx(
            'bg-zinc-900/80 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none lg:flex-1 flex w-full static lg:sticky lg:col-span-8 lg:top-[64px] h-auto lg:!h-[calc(100vh-80px)] lg:p-0 overflow-hidden  col-span-12 shadow-lg lg:mt-0',
          )}
        >
          <ErrorBoundary>
            <DynamicMap
              config={{ latitude, longitude }}
              listings={listings}
              handleViewPortChange={handleViewportChange}
            />
          </ErrorBoundary>
        </div>
        <div
          className={clsx(
            ' lg:pt-0 w-full lg:col-span-4 relative z-30 dark:bg-[#111] bg-white lg:min-h-screen order-2 lg:order-1 col-span-12 max-h-screen overflow-auto lg:h-full h-[60vh] lg:border-l border-zinc-400 dark:border-zinc-700',
          )}
        >
          <div className="sticky top-0 dark:bg-[#111] bg-snow z-20 mx-auto ">
            <header
              className={clsx(
                'flex justify-between py-4 items-center  px-6 shadow-subtler dark:shadow-lg dark:shadow-zinc-900',
              )}
            >
              <Title as="h1" size="lg">
                {`Explore ${city === null ? city : ''}`}
              </Title>
              <Button
                small
                onClick={() => setOpenModal(true)}
                className="lg:!px-5"
                withIcon={<AdjustmentsIcon className="lg:-ml-1 lg:mr-2 h-5 w-5" />}
              >
                <span className="sr-only lg:not-sr-only">Filters</span>
                {Object.values(searchFacets).filter((facet) => !!facet).length > 0 ? (
                  <span className="ml-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black text-white text-xs">
                    {Object.values(searchFacets).filter((facet) => !!facet).length}
                  </span>
                ) : null}
              </Button>
            </header>
          </div>
          {listings.length ? (
            <ul className="mx-auto pb-4 divide-y divide-zinc-300 dark:divide-zinc-700 relative z-10">
              {listings.map((listing) => (
                <li key={listing.node.id} className="relative py-8 px-6 lg:px-6 ">
                  <ListingCard listing={listing.node} loggedIn={session} replaceURL />
                </li>
              ))}
            </ul>
          ) : (
            <>
              {called && !loading ? (
                <div className="px-6 lg:px-6 text-center py-16 mx-auto ">
                  <div className="flex flex-col items-center border-2 border-dashed border-zinc-400 p-6 rounded-xl">
                    <Title as="p" size="lg">
                      {Object.keys(searchFacets).length > 0 ? 'No listings.' : 'No listings in this area.'}
                    </Title>
                    <p className="font-medium tracking-tight">
                      {Object.keys(searchFacets).length > 0
                        ? 'Adjust some filters, search a different area or:'
                        : 'Search a different area or:'}
                    </p>
                    <Button internal url="/reviews/new" className="mt-6 w-64">
                      Tell your story
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
          {loading ? (
            <div className="w-full py-16 flex items-center justify-center">
              <Loading />
            </div>
          ) : data?.queryListings?.pageInfo?.hasNextPage && !loading ? (
            <div className="w-full pb-32 flex items-center justify-center">
              <Button
                onClick={() =>
                  fetchBylatitudelongitude({
                    cursor: data?.queryListings?.pageInfo.endCursor,
                    bounds,
                    facets: searchFacets,
                  })
                }
              >
                Load more.
              </Button>
            </div>
          ) : null}
        </div>

        <ListingModal
          open={!!router.query.listingid && open}
          setOpen={setOpen}
          listing={router.query.listingid as string}
        />

        <FilterModal open={openModal} setOpen={setOpenModal} handleFilterUpdates={handleSearchUpdate} />
      </div>
    </Layout>
  )
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.query.listingid) {
    return {
      redirect: {
        destination: `/listing/${context.query.listingid}`,
        permanent: false,
      },
    }
  }
  return {
    props: {
      query: context.query,
    },
  }
}
